import { Badge, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AbsenceReasonOptions,
  Attendance,
  AttendanceRow,
  BookingExtendedView,
  BookingView,
  UserRecordAttendanceWithSignatureRequest,
} from 'cb-model';
import {
  Create,
  fetchApi,
  FormManager,
  FormRow,
  makeResetCache,
  notify,
  ParallelLoad,
  selectUser,
  useCacheRecord,
  useCloneEntity,
} from 'gossamer-front-end';
import { displayDateTimeToMinute, Entity, OptionsControlType, OptionsField } from 'gossamer-unified';
import { useFormikContext } from 'formik';
import { SignaturePad } from './SignaturePad';
import { getGpsPosition } from '../location/getGpsPosition';
import { formatEventUrl } from 'shared-front-end';

export const CaptureAttendanceESig = (): React.JSX.Element => {
  const { ticketBookingId } = useParams<{ ticketBookingId: string }>();
  const bookingRecord = useCacheRecord(BookingExtendedView, ticketBookingId, !ticketBookingId);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resetCache = makeResetCache(dispatch);

  const LocalAttendance = useCloneEntity(Attendance, (local) => {
    Object.values(local.fields).forEach((field) => {
      field.visible = false;
      field.formColumnWidths = { xl: 12 }; // every field takes up the full width of the form
    });
    local.fields.attendance_result.seq = 1; // move to top
    local.fields.attendance_result.visible = true;
    local.fields.attendance_result.mandatory = true;
    (local.fields.attendance_result as OptionsField).controlType = OptionsControlType.RadioHorizontal;
    local.fields.signature.renderEditable = SignaturePad;
    local.fields.user_comments.visible = true;
  });

  let title = bookingRecord.data?.title;
  if (bookingRecord.data?.start_at) {
    title += ` (${displayDateTimeToMinute(bookingRecord.data.start_at)})`;
  }
  return (
    <ParallelLoad responses={[bookingRecord]} ignoreErrors={false}>
      <>
        <h1>{title}</h1>
        <Stack direction="vertical" gap={2} className="mb-3">
          <div>
            record attendance for <b>{user?.name}</b>
          </div>
          {bookingRecord.data?.permit_self_sign_esig === 'yes' && (
            <div>
              <Badge bg="success">Self-signing is permitted</Badge>
            </div>
          )}
          {bookingRecord.data?.permit_self_sign_esig === 'no' && (
            <div>
              <Badge bg="danger">Self-signing is not permitted</Badge>
            </div>
          )}
        </Stack>

        <FormManager
          initialization={{
            main: {
              entity: LocalAttendance,
              type: 'create-single',
              override: {
                recording_method: 'e_sig',
              },
            },
          }}
          handleSubmit={async (values: any) => {
            try {
              const gpsPos = await getGpsPosition(1200);
              const payload: UserRecordAttendanceWithSignatureRequest = {
                eventId: bookingRecord.data?.event_id,
                signature: values.main.signature, // image of the signature
                fullName: values.main.signature_name, // full name of the signature
                userComments: values.main.user_comments,
                attendanceResult: values.main.attendance_result,
                absenceReason: values.main.absence_reason,
                gpsStatus: gpsPos.gps_status,
                gpsLatitude: gpsPos.latitude,
                gpsLongitude: gpsPos.longitude,
              };
              const response = await fetchApi('POST', `/main/user-record-attendance-signature`, payload);
              resetCache(BookingView.id, String(ticketBookingId));
              resetCache(BookingExtendedView.id, String(ticketBookingId));
              navigate(
                values.main.attendance_result === 'attended'
                  ? `/website/attend-success/${response.eventId}`
                  : formatEventUrl(title, response.eventId),
              );
            } catch (err) {
              console.error(`eSignature submit failed: ${err.error || err.message}`);
              notify([err.error || err.message || 'there is no network connection', 'danger']);
            }
          }}
        >
          <FormLogic entity={LocalAttendance} />
          <Create entity={LocalAttendance} valuesKey="main" />
        </FormManager>
      </>
    </ParallelLoad>
  );
};

interface FormLogicProps {
  entity: Entity<AttendanceRow>;
}

export const FormLogic = (props: FormLogicProps): React.JSX.Element => {
  const { values } = useFormikContext<{ main: FormRow<AttendanceRow> }>();
  props.entity.fields.signature.visible = values.main.attendance_result === 'attended';
  props.entity.fields.signature.mandatory = values.main.attendance_result === 'attended';
  props.entity.fields.signature_name.visible = values.main.attendance_result === 'attended';
  props.entity.fields.signature_name.mandatory = values.main.attendance_result === 'attended';
  props.entity.fields.absence_reason.visible =
    !!values.main.attendance_result && values.main.attendance_result !== 'attended';
  props.entity.fields.absence_reason.mandatory = values.main.attendance_result !== 'attended';
  props.entity.fields.absence_reason.mandatory = values.main.attendance_result !== 'attended';
  (props.entity.fields.absence_reason as OptionsField).options = AbsenceReasonOptions.filter(
    (option) => option.result === values.main.attendance_result,
  );
  props.entity.fields.user_comments.visible = !!values.main.attendance_result;
  return null;
};
